import type { AccountInfo, LoginInfo, PtAccount } from '@/api/types/pt_entities'
import type { BindingType, CodeOpType, CodeType } from '@/api/types/pt_enums'
import type { RequestOptions, ResponseModel } from '@/types/pt_types'
import { apiClient } from '@/axios/http_client'

export interface BindingParam {
  accountId?: number // 账号编号
  type?: BindingType // 绑定类型
  value?: string // 绑定值
  options?: RequestOptions
}
export interface ChangeSelfPasswordParam {
  oldPwd?: string // 老密码(md5)
  newPwd?: string // 新密码(md5)
  options?: RequestOptions
}
export interface ForgetPasswordParam {
  name?: string // 手机/邮箱
  password?: string // 新密码(md5)
  code?: string // 验证码
  options?: RequestOptions
}
export interface GetAccountParam {
  accountId?: number // 账号编号
  options?: RequestOptions
}
export interface GetCodeParam {
  name?: string // 手机号/邮箱
  codeType?: CodeType // 验证码类型
  codeOpType?: CodeOpType // 操作类型
  options?: RequestOptions
}
export interface LoginByCodeParam {
  name?: string // 手机号或邮箱
  code?: string // 验证码
  options?: RequestOptions
}
export interface LoginByPasswordParam {
  name?: string // 手机号/邮箱/账号
  password?: string // 密码(md5)
  options?: RequestOptions
}
export interface LogoutParam {
  options?: RequestOptions
}
export interface RefreshTokenParam {
  refreshToken?: string // 刷新令牌
  options?: RequestOptions
}
export interface SelfBindingParam {
  type?: BindingType // 绑定类型
  value?: string // 绑定值
  code?: string // 验证码
  force?: boolean // 强制绑定
  options?: RequestOptions
}
export interface SelfUnbindingParam {
  type?: BindingType // 绑定类型
  code?: string // 验证码
  options?: RequestOptions
}
export interface UnbindingParam {
  accountId?: number // 账号编号
  type?: BindingType // 绑定类型
  options?: RequestOptions
}
export interface UpdateAccountParam {
  accountId?: number // 账号编号
  request?: PtAccount // 账号信息
  options?: RequestOptions
}
export interface UpdatePasswordParam {
  accountId?: number // 账号编号
  password?: string // 新密码(md5)
  options?: RequestOptions
}

/**
 * 
 * Created by hongsq.
 */
class AccountApi {

  /**
   * 设置绑定 - 设置绑定
   * @param accountId 账号编号
   * @param type 绑定类型
   * @param value 绑定值
   */
  async binding(params: BindingParam = {}): Promise<void> {
    const {  accountId, type, value, options  } = params
    const resp = await apiClient.formPost<ResponseModel<void>>({
      data: { accountId, type, value },
      checkToken: true,
      url: '/api/account/binding',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 修改自己的密码 - 修改自己的密码
   * @param oldPwd 老密码(md5)
   * @param newPwd 新密码(md5)
   */
  async changeSelfPassword(params: ChangeSelfPasswordParam = {}): Promise<void> {
    const {  oldPwd, newPwd, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { oldPwd, newPwd },
      checkToken: true,
      url: '/api/account/password',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 忘记密码 - 通过忘记密码修改密码
   * @param name 手机/邮箱
   * @param password 新密码(md5)
   * @param code 验证码
   */
  async forgetPassword(params: ForgetPasswordParam = {}): Promise<void> {
    const {  name, password, code, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { name, password, code },
      checkToken: false,
      url: '/api/account/password/forget',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 查询账号信息 - 查询账号信息
   * @param accountId 账号编号
   */
  async getAccount(params: GetAccountParam = {}): Promise<AccountInfo> {
    const {  accountId, options  } = params
    const resp = await apiClient.get<ResponseModel<AccountInfo>>({
      params: {},
      checkToken: true,
      url: `/api/account/${accountId}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 获取验证码 - 绑定的时候直接指定手机号或邮箱,解绑的时候指定的是验证码类型
   * @param name 手机号/邮箱
   * @param codeType 验证码类型
   * @param codeOpType 操作类型
   */
  async getCode(params: GetCodeParam = {}): Promise<void> {
    const {  name, codeType, codeOpType, options  } = params
    const resp = await apiClient.get<ResponseModel<void>>({
      params: { name, codeType, codeOpType },
      checkToken: 'MAYBE',
      url: '/api/account/code',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 验证码登录 - 使用手机和验证码登录
   * @param name 手机号或邮箱
   * @param code 验证码
   */
  async loginByCode(params: LoginByCodeParam = {}): Promise<LoginInfo> {
    const {  name, code, options  } = params
    const resp = await apiClient.formPut<ResponseModel<LoginInfo>>({
      data: { name, code },
      checkToken: false,
      url: '/api/account/login/code',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 用户名密码登录 - 使用用户名密码登录
   * @param name 手机号/邮箱/账号
   * @param password 密码(md5)
   */
  async loginByPassword(params: LoginByPasswordParam = {}): Promise<LoginInfo> {
    const {  name, password, options  } = params
    const resp = await apiClient.formPut<ResponseModel<LoginInfo>>({
      data: { name, password },
      checkToken: false,
      url: '/api/account/login/pwd',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 退出登录 - 退出登录, 清空登录信息
   */
  async logout(params: LogoutParam = {}): Promise<void> {
    const {  options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: {},
      checkToken: 'MAYBE',
      url: '/api/account/logout',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 刷新AccessToken - 使用RefreshToken刷新AccessToken
   * @param refreshToken 刷新令牌
   */
  async refreshToken(params: RefreshTokenParam = {}): Promise<LoginInfo> {
    const {  refreshToken, options  } = params
    const resp = await apiClient.formPost<ResponseModel<LoginInfo>>({
      data: { refreshToken },
      checkToken: 'MAYBE',
      url: '/api/account/token/refresh',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 绑定 - 设置自己的绑定
   * @param type 绑定类型
   * @param value 绑定值
   * @param code 验证码
   * @param force 强制绑定
   */
  async selfBinding(params: SelfBindingParam = {}): Promise<void> {
    const {  type, value, code, force, options  } = params
    const resp = await apiClient.formPost<ResponseModel<void>>({
      data: { type, value, code, force },
      checkToken: true,
      url: '/api/account/self/binding',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 解绑 - 解绑自己
   * @param type 绑定类型
   * @param code 验证码
   */
  async selfUnbinding(params: SelfUnbindingParam = {}): Promise<void> {
    const {  type, code, options  } = params
    const resp = await apiClient.formPost<ResponseModel<void>>({
      data: { type, code },
      checkToken: true,
      url: '/api/account/self/unbinding',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 解绑 - 解绑
   * @param accountId 账号编号
   * @param type 绑定类型
   */
  async unbinding(params: UnbindingParam = {}): Promise<void> {
    const {  accountId, type, options  } = params
    const resp = await apiClient.formPost<ResponseModel<void>>({
      data: { accountId, type },
      checkToken: true,
      url: '/api/account/unbinding',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 修改用户 - 修改用户信息
   * @param accountId 账号编号
   * @param request 账号信息
   */
  async updateAccount(params: UpdateAccountParam = {}): Promise<void> {
    const {  accountId, request, options  } = params
    const resp = await apiClient.jsonPut<ResponseModel<void>>({
      data: { ...(request || {}) },
      checkToken: true,
      url: `/api/account/${accountId}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 设置密码 - 为设置密码
   * @param accountId 账号编号
   * @param password 新密码(md5)
   */
  async updatePassword(params: UpdatePasswordParam = {}): Promise<void> {
    const {  accountId, password, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { accountId, password },
      checkToken: true,
      url: '/api/account/set/password',
      apiOptions: options
    })
    return resp.data!
  }
}
export const accountApi = new AccountApi()
